import React from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@nextui-org/react";
import {
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineMinusCircle,
  HiOutlineExclamationTriangle,
} from "react-icons/hi2";
import { getRowLabel, getLinkedExplanation, isMeaningfulValue } from "./utils/analysis";

const WeightTag = ({ weight, t }) => {
  if (!weight || weight <= 1) return null;
  return (
    <Tooltip
      content={t("Ce critère pèse {{weight}}× plus qu'un critère standard", { weight })}
      placement="top"
      delay={200}
    >
      <span className="text-[10px] font-bold bg-primary/10 text-primary px-1.5 py-0.5 rounded-md cursor-help tabular-nums">
        ×{weight}
      </span>
    </Tooltip>
  );
};

const Explanation = ({ text, indented }) => {
  if (!text) return null;
  return (
    <div
      className={`mt-1.5 p-2 bg-gray-50 rounded-lg text-xs text-gray-500 border-l-2 border-primary/15 italic leading-relaxed ${
        indented ? "ml-7" : ""
      }`}
    >
      {text}
    </div>
  );
};

const render_diagnosis_detail = (details, t) => {
  if (details === "auto_clear") {
    return (
      <div className="flex items-center gap-1.5 mt-1.5">
        <span className="text-xs text-success bg-success/8 px-2 py-0.5 rounded-md font-medium">
          {t("analysis.clearRequest")}
        </span>
      </div>
    );
  }
  return (
    <div className="flex gap-3 mt-1.5">
      <div
        className={`flex items-center gap-1 text-xs ${
          details?.asked ? "text-success" : "text-grade-low"
        }`}
      >
        {details?.asked ? <HiOutlineCheckCircle size={14} /> : <HiOutlineXCircle size={14} />}
        <span>{t("analysis.questions")}</span>
      </div>
      <div
        className={`flex items-center gap-1 text-xs ${
          details?.reformulated ? "text-success" : "text-grade-low"
        }`}
      >
        {details?.reformulated ? (
          <HiOutlineCheckCircle size={14} />
        ) : (
          <HiOutlineXCircle size={14} />
        )}
        <span>{t("analysis.reformulation")}</span>
      </div>
    </div>
  );
};

const CriteriaList = ({ analysis, company_config, is_sider }) => {
  const { t } = useTranslation();
  const items = analysis?.items_breakdown || [];

  const to_retrieve_fields = company_config?.to_retrieve || [];
  const to_retrieve_by_name = to_retrieve_fields.reduce((acc, f) => {
    if (f?.name) acc[f.name] = f;
    return acc;
  }, {});

  const get_row_key = (row, idx) => row.key || row.field_name || `row-${idx}`;

  const get_explanation = (row) => {
    if (isMeaningfulValue(row?.score_explanation)) return String(row.score_explanation);
    if (row?.comment && isMeaningfulValue(row.comment)) return String(row.comment);
    if (row?.field_name || row?.key) {
      const linked = getLinkedExplanation(row.field_name || row.key, to_retrieve_fields, analysis);
      if (linked) return linked;
    }
    return null;
  };

  if (items.length === 0) return null;

  return (
    <div className="flex flex-col gap-1.5">
      {items.map((row, idx) => {
        const label = getRowLabel(row, to_retrieve_by_name, t, is_sider);
        const row_key = get_row_key(row, idx);
        const explanation = get_explanation(row);

        // --- 1. N/A ROWS ---
        if (row.status === "na") {
          return (
            <div
              key={row_key}
              className="flex justify-between items-center opacity-35"
            >
              <div className="flex items-center gap-2">
                <HiOutlineMinusCircle
                  size={14}
                  className="text-gray-400"
                />
                <span className="text-sm text-gray-400 line-through">{label}</span>
                {row.trigger_field && (
                  <span className="text-[10px] text-gray-400 italic">
                    ({row.trigger_field.replace(/_/g, " ")})
                  </span>
                )}
              </div>
              <span className="text-[10px] text-gray-400 font-medium bg-gray-100 px-1.5 py-0.5 rounded-md">
                N/A
              </span>
            </div>
          );
        }

        // --- 2. MISSING ROWS ---
        if (row.status === "missing") {
          return (
            <div
              key={row_key}
              className="flex justify-between items-center opacity-40"
            >
              <div className="flex items-center gap-2">
                <HiOutlineMinusCircle
                  size={14}
                  className="text-gray-400"
                />
                <span className="text-sm text-gray-400">{label}</span>
              </div>
              <span className="text-[10px] text-gray-400 font-medium bg-gray-100 px-1.5 py-0.5 rounded-md">
                Missing
              </span>
            </div>
          );
        }

        // --- 3. SPECIAL DIAGNOSIS ROWS (Sider) ---
        if (row.type === "special") {
          const is_perfect = row.score === row.max_score;
          return (
            <div
              key={row_key}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className={`flex items-center justify-center w-5 h-5 rounded ${
                      is_perfect ? "text-success" : "text-grade-medium"
                    }`}
                  >
                    {is_perfect ? (
                      <HiOutlineCheckCircle size={16} />
                    ) : (
                      <HiOutlineExclamationTriangle size={16} />
                    )}
                  </div>
                  <span className="text-foreground text-sm font-medium">{label}</span>
                </div>
                <span className="text-xs font-semibold text-gray-500 tabular-nums">
                  {row.score}/{row.max_score}
                </span>
              </div>
              <div className="ml-1">{render_diagnosis_detail(row.details, t)}</div>
              <Explanation text={explanation} />
            </div>
          );
        }

        // --- 4. NUMBER ROWS ---
        if (row.field_type === "number") {
          const score_color_bar =
            row.score >= 7 ? "bg-grade-high" : row.score >= 5 ? "bg-grade-medium" : "bg-grade-low";
          const max_score = row.max_score ?? 10;
          return (
            <div
              key={row_key}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2"
            >
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{label}</span>
                    <WeightTag
                      weight={row.weight}
                      t={t}
                    />
                  </div>
                  <span className="text-xs font-semibold text-gray-500 tabular-nums">
                    {row.score}/{max_score}
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1 overflow-hidden">
                  <div
                    className={`h-1 rounded-full transition-all duration-500 ease-out ${score_color_bar}`}
                    style={{ width: `${(row.score / max_score) * 100}%` }}
                  />
                </div>
              </div>
              <Explanation text={explanation} />
            </div>
          );
        }

        // --- 5. BOOL ROWS ---
        const is_success = row.score === 1;
        const display_success = row.bool_inverted ? row.score === 0 : is_success;

        return (
          <div
            key={row_key}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className={`flex items-center justify-center w-5 h-5 rounded ${
                    display_success ? "text-success" : "text-grade-low"
                  }`}
                >
                  {display_success ? (
                    <HiOutlineCheckCircle size={16} />
                  ) : (
                    <HiOutlineXCircle size={16} />
                  )}
                </div>
                <span
                  className={`text-sm ${
                    display_success ? "text-foreground" : "text-grade-low font-medium"
                  }`}
                >
                  {label}
                </span>
                {row.bool_inverted && (
                  <span className="text-[10px] text-orange-500 font-medium bg-orange-50 px-1.5 py-0.5 rounded-md border border-orange-100">
                    inversé
                  </span>
                )}
                <WeightTag
                  weight={row.weight}
                  t={t}
                />
              </div>
            </div>
            <Explanation
              text={explanation}
              indented
            />
          </div>
        );
      })}
    </div>
  );
};

export default CriteriaList;
